/**
 * TeenPattiGame — Main container orchestrating game flow, AI turns, state.
 * Wrapped in ErrorBoundary componentName="TeenPatti".
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { TeenPattiEngine } from '../TeenPattiEngine';
import { TeenPattiAI } from '../TeenPattiAI';
import { TeenPattiTable } from './TeenPattiTable';
import { TeenPattiBettingControls } from './TeenPattiBettingControls';
import { TeenPattiCards } from './TeenPattiCards';
import { TeenPattiHandRanking } from './TeenPattiHandRanking';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { RoomCodeDisplay } from '../../components/RoomCodeDisplay';
import { MultiplayerTeenPattiService } from '../services/MultiplayerTeenPattiService';
import { WebSocketService } from '../../services/WebSocketService';
import { NotificationService } from '../../services/NotificationService';
import { GameResult } from '../../services/multiplayer/GenericMultiplayerService';
import * as HandEvaluator from '../TeenPattiHandEvaluator';
import {
  TeenPattiGameProps, TeenPattiGameSettings, TeenPattiAction,
  VisibleTeenPattiState, TeenPattiHandResult, TeenPattiGameConfig,
} from '../types';
import './TeenPattiGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const TeenPattiGameInner: React.FC<TeenPattiGameProps> = ({ onExit, initialRoomCode }) => {
  const { isAuthenticated } = useAuthentication();
  const { addNotification } = useNotifications();

  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<TeenPattiGameSettings | null>(null);
  const [visibleState, setVisibleState] = useState<VisibleTeenPattiState | null>(null);
  const [handResult, setHandResult] = useState<TeenPattiHandResult | undefined>();
  const [showRankingGuide, setShowRankingGuide] = useState(false);

  // Multiplayer state
  const [multiplayerService] = useState<MultiplayerTeenPattiService>(
    () => new MultiplayerTeenPattiService(WebSocketService.getInstance())
  );
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');

  const engineRef = useRef(new TeenPattiEngine());
  const aiRef = useRef<Map<string, TeenPattiAI>>(new Map());
  const sessionIdRef = useRef<string | null>(null);
  const gsm = useRef(GameSessionManager.getInstance());
  const humanPlayerId = useRef('player-human');
  const hasAttemptedJoin = useRef(false);
  const isCreatingRoom = useRef(false);

  const refreshState = useCallback(() => {
    const state = engineRef.current.getVisibleState(humanPlayerId.current);
    setVisibleState(state);
    // Update hand result if player has seen cards
    const humanPlayer = state.players.find(p => p.id === humanPlayerId.current);
    if (humanPlayer?.cards) {
      setHandResult(HandEvaluator.evaluate(humanPlayer.cards));
    }
    return state;
  }, []);

  // =========================================================================
  // Multiplayer event handlers setup
  // =========================================================================
  useEffect(() => {
    multiplayerService.onGameStarted(() => {
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    });

    multiplayerService.onMoveUpdate((move: TeenPattiAction, gameState: any) => {
      if (gameState) {
        setVisibleState(gameState);
      }
    });

    multiplayerService.onGameOver((result: GameResult) => {
      const message = result.result === 'win'
        ? `🎉 ${result.winner} wins!`
        : 'Game over!';
      NotificationService.getInstance().sendInfoNotification(message);
    });

    multiplayerService.onPlayerDisconnected(() => {
      setOpponentConnected(false);
      NotificationService.getInstance().sendWarningNotification(
        'Opponent disconnected. Waiting for reconnection...'
      );
    });

    multiplayerService.onPlayerConnected(() => {
      setOpponentConnected(true);
      NotificationService.getInstance().sendSuccessNotification('Opponent reconnected!');
    });

    multiplayerService.onGameStateReceived((gameState: any) => {
      if (gameState) {
        setVisibleState(gameState);
      }
    });

    multiplayerService.onConnectionStatus((status) => {
      setConnectionStatus(status);
      if (status === 'reconnecting') {
        NotificationService.getInstance().sendWarningNotification('Connection lost. Reconnecting...');
      } else if (status === 'connected') {
        NotificationService.getInstance().sendSuccessNotification('Connected!');
      } else if (status === 'disconnected') {
        NotificationService.getInstance().sendErrorNotification('Disconnected from server');
      }
    });

    multiplayerService.onError((error: string) => {
      NotificationService.getInstance().sendErrorNotification(`Multiplayer error: ${error}`);
    });
  }, [multiplayerService]);

  // =========================================================================
  // Deep link room code handling
  // =========================================================================
  useEffect(() => {
    if (initialRoomCode && !roomCode && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;
      setView('playing');
      setRoomCode(initialRoomCode);
      setWaitingForOpponent(true);

      multiplayerService.joinRoom(initialRoomCode)
        .then(() => {
          setWaitingForOpponent(false);
          setOpponentConnected(true);
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Unable to join room ${initialRoomCode}. ${error.message || 'Please check the room code.'}`
          );
          setView('mode-select');
          setWaitingForOpponent(false);
          setRoomCode(null);
        });
    }
  }, [initialRoomCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartGame = useCallback((newSettings: TeenPattiGameSettings) => {
    // Handle multiplayer room creation
    if (newSettings.mode === 'multiplayer-realtime') {
      if (isCreatingRoom.current) return;
      isCreatingRoom.current = true;

      multiplayerService.createRoom()
        .then((room) => {
          setRoomCode(room.code);
          setWaitingForOpponent(true);
          setSettings(newSettings);
          setView('playing');
          isCreatingRoom.current = false;
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Failed to create room: ${error.message || 'Unknown error'}`
          );
          isCreatingRoom.current = false;
        });
      return;
    }

    const engine = engineRef.current;
    const aiCount = newSettings.aiOpponentCount || 3;
    const totalPlayers = aiCount + 1;

    const config: TeenPattiGameConfig = {
      tableSize: totalPlayers,
      buyIn: newSettings.buyIn || 1000,
      bootAmount: newSettings.bootAmount || 10,
      potLimit: newSettings.potLimit ?? null,
      players: [
        { id: humanPlayerId.current, name: 'You', isHuman: true },
        ...Array.from({ length: aiCount }, (_, i) => ({
          id: `ai-${i + 1}`,
          name: `Bot ${i + 1}`,
          isHuman: false,
          aiDifficulty: newSettings.aiDifficulty || 'medium' as const,
        })),
      ],
    };

    engine.initializeGame(config);

    // Create AI instances
    aiRef.current.clear();
    config.players.filter(p => !p.isHuman).forEach(p => {
      aiRef.current.set(p.id, new TeenPattiAI(p.aiDifficulty || 'medium'));
    });

    setSettings(newSettings);
    setView('playing');
    setHandResult(undefined);
    sessionIdRef.current = gsm.current.startGameSession('teen-patti');

    // Start first round
    engine.startNewRound();
    refreshState();

    addNotification({
      type: NotificationType.SUCCESS,
      title: 'Game Started',
      message: `Teen Patti - ${newSettings.aiDifficulty || 'medium'} difficulty`,
      duration: 2000,
    });

    // Check if AI goes first
    scheduleAITurnIfNeeded();
  }, [addNotification, refreshState, multiplayerService]);

  const scheduleAITurnIfNeeded = useCallback(() => {
    const engine = engineRef.current;
    const currentId = engine.getCurrentPlayer();
    if (!currentId || currentId === humanPlayerId.current) return;
    if (engine.isGameOver() || !engine.isRoundInProgress()) return;

    const ai = aiRef.current.get(currentId);
    if (!ai) return;

    const state = engine.getVisibleState(currentId);
    const validActions = engine.getValidActions(currentId);

    ai.getAction(state, currentId, validActions).then(action => {
      if (engine.isGameOver() || !engine.isRoundInProgress()) return;

      // Handle see-cards: AI sees then needs to act again
      if (action.type === 'see-cards') {
        engine.seeCards(currentId);
        const newValidActions = engine.getValidActions(currentId);
        const newState = engine.getVisibleState(currentId);
        return ai.getAction(newState, currentId, newValidActions).then(nextAction => {
          processAction(currentId, nextAction);
        });
      }

      processAction(currentId, action);
    });
  }, []);

  const processAction = useCallback((playerId: string, action: TeenPattiAction) => {
    const engine = engineRef.current;
    const result = engine.submitAction(playerId, action);

    if (result.success) {
      if (result.action) {
        const actionName = result.action.action;
        const playerName = result.action.playerName;
        if (actionName === 'pack') {
          addNotification({ type: NotificationType.INFO, title: `${playerName} packed`, message: '', duration: 1500 });
        } else if (actionName === 'raise') {
          addNotification({ type: NotificationType.INFO, title: `${playerName} raised ${result.action.amount}`, message: '', duration: 1500 });
        }
      }

      // Handle sideshow pending
      if (result.sideshowPending) {
        const gs = engine.getGameState();
        const req = gs.pendingSideshow;
        if (req) {
          const targetAI = aiRef.current.get(req.targetId);
          if (targetAI) {
            const state = engine.getVisibleState(req.targetId);
            targetAI.respondToSideshow(state, req.targetId, req.requesterId).then(accept => {
              engine.respondToSideshow(req.targetId, accept);
              const newState = refreshState();
              checkRoundEnd(newState);
              scheduleAITurnIfNeeded();
            });
            return;
          }
          // Human is target - for now auto-accept (would need UI prompt in full impl)
          engine.respondToSideshow(req.targetId, true);
        }
      }

      const newState = refreshState();

      if (result.roundComplete) {
        handleRoundComplete(newState);
        return;
      }

      checkRoundEnd(newState);
      scheduleAITurnIfNeeded();
    }
  }, [addNotification, refreshState]);

  const checkRoundEnd = useCallback((state: VisibleTeenPattiState) => {
    if (state.gameStatus.type === 'complete') {
      handleGameOver();
    }
  }, []);

  const handleRoundComplete = useCallback((state: VisibleTeenPattiState) => {
    const engine = engineRef.current;
    const lastRound = engine.getGameState().roundHistory;
    const latest = lastRound[lastRound.length - 1];

    if (latest) {
      addNotification({
        type: NotificationType.SUCCESS,
        title: `Round ${latest.roundNumber} Complete`,
        message: `${latest.winnerName} wins ${latest.potAmount} chips!`,
        duration: 3000,
      });
    }

    if (state.gameStatus.type === 'complete') {
      handleGameOver();
      return;
    }

    // Start next round after delay
    setTimeout(() => {
      engine.startNewRound();
      setHandResult(undefined);
      refreshState();
      scheduleAITurnIfNeeded();
    }, 2000);
  }, [addNotification, refreshState]);

  const handleGameOver = useCallback(() => {
    setView('game-over');
    if (sessionIdRef.current) {
      gsm.current.endGameSession(sessionIdRef.current, true);
      sessionIdRef.current = null;
    }
    const winner = engineRef.current.getWinner();
    const isHumanWinner = winner === humanPlayerId.current;
    addNotification({
      type: isHumanWinner ? NotificationType.SUCCESS : NotificationType.INFO,
      title: '🎉 Game Over!',
      message: isHumanWinner ? 'Congratulations! You won!' : 'Better luck next time!',
      duration: 5000,
    });
  }, [addNotification]);

  /** Join a private online game room */
  const handleJoinPrivateGame = useCallback(async (code: string) => {
    setRoomCode(code);
    setWaitingForOpponent(true);
    setView('playing');

    try {
      await multiplayerService.joinRoom(code);
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    } catch (error: any) {
      NotificationService.getInstance().sendErrorNotification(
        `Unable to join room ${code}. ${error.message || 'Please check the room code.'}`
      );
      setView('mode-select');
      setWaitingForOpponent(false);
      setRoomCode(null);
    }
  }, [multiplayerService]);

  const handleCancelWaiting = useCallback(() => {
    multiplayerService.leaveGame();
    setWaitingForOpponent(false);
    setRoomCode(null);
    setView('mode-select');
    setSettings(null);
    isCreatingRoom.current = false;
  }, [multiplayerService]);

  const handlePlayerAction = useCallback((action: TeenPattiAction) => {
    const engine = engineRef.current;
    if (action.type === 'see-cards') {
      engine.seeCards(humanPlayerId.current);
      refreshState();
      return;
    }
    processAction(humanPlayerId.current, action);
  }, [processAction, refreshState]);

  const handleNewGame = useCallback(() => {
    if (roomCode) {
      multiplayerService.leaveGame();
    }
    aiRef.current.clear();
    setView('mode-select');
    setVisibleState(null);
    setHandResult(undefined);
    setSettings(null);
    setRoomCode(null);
    setWaitingForOpponent(false);
    setOpponentConnected(false);
    setConnectionStatus('connected');
    isCreatingRoom.current = false;
  }, [roomCode, multiplayerService]);

  const handleExit = useCallback(() => {
    if (sessionIdRef.current) {
      gsm.current.endGameSession(sessionIdRef.current, false);
      sessionIdRef.current = null;
    }
    onExit?.();
  }, [onExit]);

  // Trigger AI on initial load if AI goes first
  useEffect(() => {
    if (view === 'playing' && visibleState) {
      scheduleAITurnIfNeeded();
    }
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const tpConfig: GameModeConfig = {
      gameName: 'Teen Patti',
      gameIcon: '🃏',
      subtitle: 'Indian card game',
      availableModes: ['ai', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: (selection.difficulty as any) || 'medium',
          aiOpponentCount: 3,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime' });
      }
    };

    return (
      <div className="tp-game">
        <UniversalGameModeSelector
          config={tpConfig}
          onStart={handleModeSelection}
          onJoinRoom={handleJoinPrivateGame}
          onCreateRoom={() => handleStartGame({ mode: 'multiplayer-realtime' })}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!visibleState) {
    if (waitingForOpponent && roomCode) {
      return (
        <div className="tp-game">
          <RoomCodeDisplay
            roomCode={roomCode}
            gameType="poker"
            playerName="Guest"
            maxPlayers={6}
            onCancel={handleCancelWaiting}
            showCancelButton={true}
          />
        </div>
      );
    }
    return null;
  }

  const humanPlayer = visibleState.players.find(p => p.id === humanPlayerId.current);
  const isHumanTurn = visibleState.players[visibleState.currentPlayerIndex]?.id === humanPlayerId.current;
  const validActions = isHumanTurn ? engineRef.current.getValidActions(humanPlayerId.current) : [];
  const isBlind = humanPlayer?.blindSeenStatus === 'blind';
  const currentStake = visibleState.currentStake;

  const gameOverConfig = view === 'game-over' ? {
    show: true,
    result: (engineRef.current.getWinner() === humanPlayerId.current ? 'win' : 'loss') as 'win' | 'loss',
    title: '🎉 Game Over!',
    subtitle: engineRef.current.getWinner() === humanPlayerId.current
      ? '🏆 You Won!'
      : `${engineRef.current.getGameState().players.find(p => p.id === engineRef.current.getWinner())?.name || 'AI'} Wins!`,
    icon: engineRef.current.getWinner() === humanPlayerId.current ? '🏆' : '😔',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Teen Patti',
    board: (
      <div className="tp-game__table-area">
        <TeenPattiTable
          gameState={visibleState}
          currentPlayerId={humanPlayerId.current}
          onAction={handlePlayerAction}
          isMultiplayer={false}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'cards',
        title: 'Your Cards',
        icon: '🃏',
        content: (
          <div className="tp-game__player-cards">
            {humanPlayer && (
              <TeenPattiCards
                cards={humanPlayer.cards}
                size="large"
                faceDown={!humanPlayer.cards}
                isBlind={isBlind || false}
                animateReveal={!isBlind}
              />
            )}
            {handResult && (
              <TeenPattiHandRanking
                handResult={handResult}
                showRankingGuide={showRankingGuide}
              />
            )}
            <button
              className="tp-game__guide-toggle"
              onClick={() => setShowRankingGuide(!showRankingGuide)}
            >
              {showRankingGuide ? 'Hide Rankings' : 'Show Rankings'}
            </button>
          </div>
        ),
      },
      {
        id: 'betting',
        title: 'Betting',
        icon: '💰',
        content: isHumanTurn && humanPlayer ? (
          <TeenPattiBettingControls
            validActions={validActions}
            pot={visibleState.pot}
            currentStake={currentStake}
            minBet={isBlind ? currentStake : currentStake * 2}
            maxBet={isBlind ? currentStake * 2 : currentStake * 4}
            playerStack={humanPlayer.chipStack}
            isBlind={isBlind || false}
            onAction={handlePlayerAction}
            disabled={!isHumanTurn}
          />
        ) : (
          <div style={{ padding: '8px', opacity: 0.6 }}>Waiting for your turn...</div>
        ),
      },
    ],
    statusBar: {
      turnIndicator: (
        <span>
          {view === 'game-over'
            ? 'Game Over'
            : isHumanTurn
              ? '🎯 Your Turn'
              : `⏳ Waiting for ${visibleState.players[visibleState.currentPlayerIndex]?.name}...`}
        </span>
      ),
      gameStatus: view === 'game-over' ? 'Game Over' : `Round ${visibleState.roundNumber}`,
      gameStatusType: view === 'game-over' ? 'ended' : 'active',
      connectionStatus: settings?.mode === 'multiplayer-realtime' ? connectionStatus : undefined,
      roomCode: roomCode || undefined,
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
    roomCode: roomCode || undefined,
  };

  return (
    <div className="tp-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

const TeenPattiGame: React.FC<TeenPattiGameProps> = (props) => (
  <ErrorBoundary componentName="TeenPatti">
    <TeenPattiGameInner {...props} />
  </ErrorBoundary>
);

export { TeenPattiGame };
export default TeenPattiGame;
